import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Zap, 
  BookOpen, 
  Clock, 
  BarChart3, 
  Play, 
  Pause,
  Sun,
  Moon,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { splitWordByORP } from '../hooks/useRSVP';

const DEMO_TEXT = "Speed reading is the art of silencing subvocalization. Most readers have an average reading speed of 200 words per minute, which is about as fast as they can talk. This is because they are subconsciously speaking the words as they read them. With RSVP technology, you can train your brain to process words visually without the need to speak them internally. This breakthrough allows you to read at speeds of 500 words per minute or more while maintaining excellent comprehension. Start your speed reading journey today and unlock your full reading potential.";

export default function Landing() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  
  const [demoWords] = useState(DEMO_TEXT.split(' '));
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [wpm, setWpm] = useState(300);
  
  // Demo RSVP animation
  useEffect(() => {
    if (!isPlaying) return;
    
    const interval = 60000 / wpm;
    const timer = setInterval(() => {
      setCurrentIndex(prev => {
        if (prev >= demoWords.length - 1) {
          setIsPlaying(false);
          return 0;
        }
        return prev + 1;
      });
    }, interval);
    
    return () => clearInterval(timer);
  }, [isPlaying, wpm, demoWords.length]);
  
  const currentWord = demoWords[currentIndex] || '';
  const wordParts = splitWordByORP(currentWord);
  
  const features = [
    {
      icon: Zap,
      title: "Read 3x Faster",
      description: "Train your brain to process words at speeds up to 1000+ WPM"
    },
    {
      icon: BookOpen,
      title: "Any Document",
      description: "Upload PDFs, EPUBs, or text files and start reading instantly"
    },
    {
      icon: Clock,
      title: "Save Time",
      description: "Finish books in hours instead of days with focused reading"
    },
    {
      icon: BarChart3,
      title: "Track Progress",
      description: "Monitor your reading stats and watch your speed improve"
    }
  ];

  return (
    <div className="min-h-screen bg-background transition-theme">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl">SpeedRead</span>
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            
            {user ? (
              <Button 
                onClick={() => navigate('/dashboard')}
                className="btn-pill"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  data-testid="login-btn"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/register')}
                  className="btn-pill"
                  data-testid="get-started-btn"
                >
                  Get Started
                </Button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Value Proposition */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="font-heading font-extrabold text-responsive-hero leading-tight mb-6">
                Read Books at
                <span className="text-primary"> Lightning Speed</span>
              </h1>
              <p className="text-responsive-subhead text-muted-foreground mb-8 max-w-lg">
                RSVP technology displays one word at a time, eliminating eye movement and doubling your reading speed while improving comprehension.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/register')}
                  className="btn-pill gap-2"
                  data-testid="hero-cta-btn"
                >
                  Start Reading Faster
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="btn-pill gap-2"
                  data-testid="try-demo-btn"
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  Try Demo
                </Button>
              </div>
            </motion.div>

            {/* Right: Live RSVP Demo */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-card rounded-2xl border border-border p-8 shadow-lg">
                <div className="text-center mb-6">
                  <span className="text-sm font-medium text-muted-foreground">
                    Live Demo • {wpm} WPM
                  </span>
                </div>
                
                {/* RSVP Display */}
                <div 
                  className="h-32 flex items-center justify-center bg-muted/50 rounded-xl mb-6"
                  data-testid="demo-rsvp-display"
                >
                  <span className="font-reader text-4xl md:text-5xl tracking-wide">
                    <span className="text-foreground">{wordParts.before}</span>
                    <span className="orp-highlight">{wordParts.orp}</span>
                    <span className="text-foreground">{wordParts.after}</span>
                  </span>
                </div>
                
                {/* Controls */}
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        setCurrentIndex(0);
                        setIsPlaying(false);
                      }}
                      data-testid="demo-reset-btn"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                        <path d="M3 3v5h5" />
                      </svg>
                    </Button>
                    <Button
                      size="lg"
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="btn-pill px-8"
                      data-testid="demo-play-btn"
                    >
                      {isPlaying ? (
                        <>
                          <Pause className="w-5 h-5 mr-2" /> Pause
                        </>
                      ) : (
                        <>
                          <Play className="w-5 h-5 mr-2" /> Play
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Speed Slider */}
                  <div className="px-4">
                    <div className="flex justify-between text-sm text-muted-foreground mb-2">
                      <span>Speed</span>
                      <span>{wpm} WPM</span>
                    </div>
                    <Slider
                      value={[wpm]}
                      onValueChange={([value]) => setWpm(value)}
                      min={100}
                      max={800}
                      step={50}
                      data-testid="demo-speed-slider"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>100</span>
                      <span>800</span>
                    </div>
                  </div>
                </div>
                
                {/* Progress */}
                <div className="mt-4 bg-muted rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="bg-primary h-full transition-all duration-100"
                    style={{ width: `${(currentIndex / demoWords.length) * 100}%` }}
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl mb-4">
              Why Speed Readers Choose Us
            </h2>
            <p className="text-muted-foreground text-responsive-subhead max-w-2xl mx-auto">
              Our RSVP technology is scientifically designed to maximize your reading potential
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-xl p-6 border border-border card-hover"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl mb-4">
              Ready to Transform Your Reading?
            </h2>
            <p className="text-muted-foreground text-responsive-subhead mb-8">
              Join thousands of readers who have unlocked their speed reading potential
            </p>
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
              className="btn-pill gap-2"
              data-testid="cta-get-started-btn"
            >
              Get Started Free
              <ChevronRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
              <Zap className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold">SpeedRead</span>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2025 SpeedRead. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
